/**
 * SitLabelSummary Component
 * Reusable component for displaying SITs/Labels in deployment summaries
 * Used in both Dashboard (multi-tenant) and Tenant (single-tenant) deployment dialogs
 */

import React from 'react';
import { Text } from '@fluentui/react-components';
import {
  DocumentBulletList20Regular,
  Tag20Regular,
} from '@fluentui/react-icons';
import { SIT_LABEL_VARIANT_COLORS } from '../constants';
import type { SummaryEntity } from '../utils';
import './summary.css';

interface SitLabelSummaryProps {
  sitsLabel: string;
  labelsLabel: string;
  sits: SummaryEntity[];
  labels: SummaryEntity[];
  variant?: 'existing' | 'new';
}

/**
 * Renders a two-column grid showing SITs and Labels
 * Supports two variants:
 * - 'existing': Gray/neutral styling for items already in tenant
 * - 'new': Blue/accent styling for items to be created
 */
export const SitLabelSummary: React.FC<SitLabelSummaryProps> = ({
  sitsLabel,
  labelsLabel,
  sits,
  labels,
  variant = 'existing',
}) => {
  const isNew = variant === 'new';
  const variantColors = SIT_LABEL_VARIANT_COLORS[variant];
  const iconColor = variantColors.icon;
  const textColor = variantColors.text;

  return (
    <div className="summary-grid-two-col">
      {/* SITs Column */}
      <div>
        <div className="summary-icon-header">
          <DocumentBulletList20Regular style={{ color: iconColor }} />
          <Text style={{ fontWeight: 600, fontSize: 13, color: textColor }}>
            {sitsLabel} ({sits.length})
          </Text>
        </div>
        {sits.length > 0 ? (
          <ul className={isNew ? 'summary-list' : 'summary-list summary-list--existing'}>
            {/* Keyed on the internal match key, not the display text: two entities can share
                display text but never share a match key. */}
            {sits.map((sit) => (
              <li key={sit.key} style={{ marginBottom: 3 }}>{sit.display}</li>
            ))}
          </ul>
        ) : (
          <Text style={{ fontSize: 13, color: '#888', paddingLeft: 28 }}>None</Text>
        )}
      </div>

      {/* Labels Column */}
      <div>
        <div className="summary-icon-header">
          <Tag20Regular style={{ color: iconColor }} />
          <Text style={{ fontWeight: 600, fontSize: 13, color: textColor }}>
            {labelsLabel} ({labels.length})
          </Text>
        </div>
        {labels.length > 0 ? (
          <ul className={isNew ? 'summary-list' : 'summary-list summary-list--existing'}>
            {labels.map((label) => (
              <li key={label.key} style={{ marginBottom: 3 }}>{label.display}</li>
            ))}
          </ul>
        ) : (
          <Text style={{ fontSize: 13, color: '#888', paddingLeft: 28 }}>None</Text>
        )}
      </div>
    </div>
  );
};

export default SitLabelSummary;
